/**
 * Die-chip presentation (HEX-139; reframed by ADR-0024). Wherever a die is shown
 * — packed in the loadout grid (ADR-0024 §2/§5), or as a level-up / reward offer
 * — it must read as a DIE, not a card-styled tile. This is the shared die chip: a
 * pip-faced die square + name + face-set line. Pure DOM, no behaviour, no
 * engine/data change — `DieType` is read only.
 *
 * The separate under-grid dice TRAY (ADR-0022 §4) is retired by ADR-0024: dice
 * now cost loadout cells, so the grid is the single source. `renderGridDieChip`
 * is the in-grid renderer; the loadout grid host wires positioning + interaction.
 */

import { type DieType, PLAIN_D6 } from '../dice';
import type { DieFace } from '../types';
import { facePips } from './dieGeometry';

/** The pip face a chip shows for a multi-face die: its modal (highest-weight)
 * face, tie-broken to the highest value — the die's "signature" face. A plain
 * d6 has no signature, so callers read {@link facesLabel} for the set. */
export function signatureFace(die: DieType): DieFace {
  let bestFace: DieFace = 6;
  let bestWeight = -1;
  for (const fw of die.faces) {
    if (fw.weight > bestWeight || (fw.weight === bestWeight && fw.face > bestFace)) {
      bestFace = fw.face;
      bestWeight = fw.weight;
    }
  }
  return bestFace;
}

/** Whether `die` is the uniform six-face d6 (no signature face). */
export function isPlainD6(die: DieType): boolean {
  if (die.id === PLAIN_D6.id) return true;
  return die.faces.length === 6;
}

/** The face-set descriptor under a chip: `d6` for the plain die, else the
 * allowed faces ascending (`4·5·6`). Communicates the guarantee at a glance. */
export function facesLabel(die: DieType): string {
  if (isPlainD6(die)) return 'd6';
  return die.faces
    .map((fw) => fw.face)
    .sort((a, b) => a - b)
    .join('·');
}

/** A 3×3 pip face for `face` (lit cells carry `--lit`). The flat 2D analogue of
 * the combat cube's face — same `facePips` lookup, no 3D. */
function buildPipFace(face: DieFace): HTMLElement {
  const grid = document.createElement('div');
  grid.className = 'hex6-die-chip__face';
  const lit = facePips(face);
  for (let cell = 1; cell <= 9; cell++) {
    const pip = document.createElement('span');
    pip.className = lit.includes(cell)
      ? 'hex6-die-chip__pip hex6-die-chip__pip--lit'
      : 'hex6-die-chip__pip';
    grid.appendChild(pip);
  }
  return grid;
}

export interface DieChipOptions {
  /** `tray` — compact chip (default). `offer` — fills its container for the
   * level-up / reward offer tile. `grid` — fills its 1×1 loadout cell when packed
   * in the grid (ADR-0024 §5). */
  readonly variant?: 'tray' | 'offer' | 'grid';
}

/** One owned-die chip: pip-face die square + name + face-set line. Reads as a
 * die, never a card (ADR-0022 §4). `data-die-id` carries the type id. */
export function renderDieChip(die: DieType, opts: DieChipOptions = {}): HTMLElement {
  const chip = document.createElement('div');
  chip.className = 'hex6-die-chip';
  if (opts.variant === 'offer') chip.classList.add('hex6-die-chip--offer');
  if (opts.variant === 'grid') chip.classList.add('hex6-die-chip--grid');
  chip.dataset.dieId = die.id;

  const plain = isPlainD6(die);
  // A plain d6 has no signature face — show the canonical 6 so it still reads as
  // a die; the faces line clarifies it is the uniform d6.
  chip.appendChild(buildPipFace(plain ? 6 : signatureFace(die)));

  const name = document.createElement('div');
  name.className = 'hex6-die-chip__name';
  name.textContent = die.name;

  const faces = document.createElement('div');
  faces.className = 'hex6-die-chip__faces';
  faces.textContent = facesLabel(die);

  chip.append(name, faces);
  return chip;
}

/** A die-card's in-grid chip: the pip die-chip sized to fill its 1×1 loadout cell
 * (ADR-0024 §5). The host (inventory editor / combat overlay) sets the cell's
 * grid-area and wires drag / select; this is presentation only. */
export function renderGridDieChip(die: DieType): HTMLElement {
  return renderDieChip(die, { variant: 'grid' });
}
